import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { TypographyMuted, TypographyXS } from '@/components/ui/typography';
import { ResearchedProduct } from '@/types/MarketResearch';
import { useCorrectPublishedResearchMutation } from '@/queries/useCorrectPublishedResearchMutation';
import { useNonResearchReasonQuery } from '@/queries/useNonResearchReasonQuery';
import Button from '@/components/Button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Edit3, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface CorrectResearchDialogProps {
  isOpen: boolean;
  onClose: () => void;
  product: ResearchedProduct | null;
}

const CorrectResearchDialog = ({
  isOpen,
  onClose,
  product,
}: CorrectResearchDialogProps) => {
  const [isNotSearched, setIsNotSearched] = useState(false);
  const [precoCompra, setPrecoCompra] = useState<string>('');
  const [volumeCompra, setVolumeCompra] = useState<string>('');
  const [precoVenda, setPrecoVenda] = useState<string>('');
  const [motivoId, setMotivoId] = useState<string>('');
  const [justificativa, setJustificativa] = useState<string>('');

  const { data: motivos, isLoading: isLoadingMotivos } =
    useNonResearchReasonQuery();
  const correctMutation = useCorrectPublishedResearchMutation();

  useEffect(() => {
    if (product) {
      setPrecoCompra(
        product.VL_PRECO_COMPRA ? String(product.VL_PRECO_COMPRA) : '',
      );
      setVolumeCompra(
        product.QTD_VOLUME_COMPRA ? String(product.QTD_VOLUME_COMPRA) : '',
      );
      setPrecoVenda(
        product.VL_PRECO_VENDA ? String(product.VL_PRECO_VENDA) : '',
      );
      setMotivoId('');
      setJustificativa('');
      setIsNotSearched(false);
    }
  }, [product, isOpen]);

  if (!product) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isNotSearched && !motivoId) {
      toast.error('Por favor, selecione um motivo de não pesquisa.');
      return;
    }

    if (!justificativa.trim()) {
      toast.error(
        'Informe a justificativa para a alteração da pesquisa publicada.',
      );
      return;
    }

    try {
      await correctMutation.mutateAsync({
        SK_PRODUTOS_PESQUISADOS: product.SK_PRODUTOS_PESQUISADOS,
        VL_PRECO_COMPRA: isNotSearched ? null : Number(precoCompra) || 0,
        QTD_VOLUME_COMPRA: isNotSearched ? null : Number(volumeCompra) || 0,
        VL_PRECO_VENDA: isNotSearched ? null : Number(precoVenda) || 0,
        SK_MOTIVO_NAO_PESQUISA: isNotSearched ? Number(motivoId) : null,
        DS_JUSTIFICATIVA: justificativa.trim(),
      });

      toast.success('Pesquisa publicada corrigida com sucesso!');
      onClose();
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        'Erro ao realizar correção da pesquisa.';
      toast.error(msg);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-[550px]" aria-describedby={undefined}>
        <DialogHeader>
          <div className="flex items-center gap-2 text-primary">
            <Edit3 size={20} />
            <DialogTitle>Correção de Pesquisa Publicada</DialogTitle>
          </div>
          <TypographyMuted>
            Apenas Administradores da Revenda podem alterar pesquisas
            publicadas. Toda alteração é registrada em auditoria.
          </TypographyMuted>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="p-3 bg-slate-50 border rounded-lg space-y-1">
            <TypographyXS className="text-gray-500 font-semibold">
              ITEM EM CORREÇÃO
            </TypographyXS>
            <p className="text-sm font-bold text-gray-900">
              {product.DS_PRODUTO}
            </p>
            <p className="text-xs text-gray-600">
              PDV:{' '}
              <span className="font-medium">{product.DS_NOME_FANTASIA}</span> |
              Revenda: <span className="font-medium">{product.DS_REVENDA}</span>
            </p>
          </div>

          <div className="flex items-center gap-3">
            <label className="text-xs font-semibold text-gray-700 cursor-pointer flex items-center gap-2">
              <input
                type="checkbox"
                checked={isNotSearched}
                onChange={(e) => setIsNotSearched(e.target.checked)}
                className="rounded border-gray-300 text-primary focus:ring-primary h-4 w-4"
              />
              Marcar como Não Pesquisado
            </label>
          </div>

          {isNotSearched ? (
            <div className="space-y-2">
              <Label htmlFor="motivo">Motivo de Não Pesquisa *</Label>
              {isLoadingMotivos ? (
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <Loader2 size={14} className="animate-spin" /> Carregando
                  motivos...
                </div>
              ) : (
                <select
                  id="motivo"
                  value={motivoId}
                  onChange={(e) => setMotivoId(e.target.value)}
                  className="w-full h-10 px-3 border rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                >
                  <option value="">Selecione o motivo...</option>
                  {motivos?.map((m: any) => (
                    <option
                      key={m.SK_MOTIVO_NAO_PESQUISA}
                      value={m.SK_MOTIVO_NAO_PESQUISA}
                    >
                      {m.DS_MOTIVO_NAO_PESQUISA}
                    </option>
                  ))}
                </select>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label htmlFor="precoCompra" className="text-xs font-semibold">
                  Preço Compra (R$)
                </Label>
                <Input
                  id="precoCompra"
                  type="number"
                  step="0.01"
                  min="0"
                  value={precoCompra}
                  onChange={(e) => setPrecoCompra(e.target.value)}
                  placeholder="0,00"
                />
              </div>

              <div>
                <Label htmlFor="volumeCompra" className="text-xs font-semibold">
                  Qtd. Volume
                </Label>
                <Input
                  id="volumeCompra"
                  type="number"
                  step="0.01"
                  min="0"
                  value={volumeCompra}
                  onChange={(e) => setVolumeCompra(e.target.value)}
                  placeholder="0"
                />
              </div>

              <div>
                <Label htmlFor="precoVenda" className="text-xs font-semibold">
                  Preço Venda (R$)
                </Label>
                <Input
                  id="precoVenda"
                  type="number"
                  step="0.01"
                  min="0"
                  value={precoVenda}
                  onChange={(e) => setPrecoVenda(e.target.value)}
                  placeholder="0,00"
                />
              </div>
            </div>
          )}

          <div>
            <Label
              htmlFor="justificativa"
              className="text-xs font-semibold flex items-center justify-between"
            >
              <span>Justificativa da Correção *</span>
              <span className="text-[10px] text-amber-700 font-normal">
                Obrigatório para Auditoria
              </span>
            </Label>
            <Textarea
              id="justificativa"
              rows={3}
              value={justificativa}
              onChange={(e) => setJustificativa(e.target.value)}
              placeholder="Descreva o motivo desta alteração (ex: erro de digitação do representante)..."
              required
              className="mt-1 text-sm"
            />
          </div>

          <DialogFooter className="pt-3 border-t flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={correctMutation.isPending}
              className="gap-2"
            >
              {correctMutation.isPending && (
                <Loader2 size={15} className="animate-spin" />
              )}
              Salvar Correção
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CorrectResearchDialog;
